import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Award, PlayCircle, FileText, CheckCircle, Lock, Clock, Presentation, Download, FileEdit, ShoppingBag, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { createCheckout } from '../../services/api';
import { ProgressBadge } from '../ProgressComponents';

const OverviewTab = ({
  currentFormula, isKit, kitBlocNum, entitlements, programEntitlements,
  subscriptionInfo, user, confirmedBookings, selectedProgramId,
  enrolledProgram, userCourses, allCourses,
  handleCourseClick, setActiveTab,
  kitHasElearning, kitDownloadPath, currentKitData,
  decouverteBooking
}) => {
  return (
          <div>
            {/* Formula Badge */}
            {currentFormula && user.role !== 'admin' && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.4rem 1rem', borderRadius: '2rem', marginBottom: '1.5rem',
                background: currentFormula === 'gold' ? '#fdf6e3' : currentFormula === 'silver' ? '#f0e6f6' : isKit ? '#EBF5FF' : '#f5e6d3',
                border: `2px solid ${currentFormula === 'gold' ? '#d4af37' : currentFormula === 'silver' ? '#9b59b6' : isKit ? '#3B82F6' : '#c4956a'}`,
                color: currentFormula === 'gold' ? '#b8960c' : currentFormula === 'silver' ? '#8e44ad' : isKit ? '#1e40af' : '#8B6914',
                fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase'
              }} data-testid="formula-badge">
                {isKit ? `Kit Bloc ${kitBlocNum}` : `Formule ${entitlements.label}`}
              </div>
            )}

            {/* Subscription Progress Bar */}
            {subscriptionInfo && user.role !== 'admin' && (
              <div className="card" style={{
                marginBottom: '1.5rem',
                padding: '1.25rem 1.5rem',
                background: subscriptionInfo.expired
                  ? 'linear-gradient(135deg, #FEE2E2, #fff)'
                  : 'linear-gradient(135deg, #f0fdf4 0%, #fff 100%)',
                border: `1px solid ${subscriptionInfo.expired ? '#fca5a5' : '#bbf7d040'}`
              }} data-testid="subscription-progress">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div>
                    <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: subscriptionInfo.expired ? '#DC2626' : '#166534', marginBottom: '0.25rem' }}>
                      {subscriptionInfo.expired ? 'Accès expiré' : 'Durée de votre accès'}
                    </h3>
                    <p style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                      {subscriptionInfo.expired
                        ? 'Votre accès à la plateforme a expiré. Contactez-nous pour le renouveler.'
                        : `${Math.max(0, Math.ceil((subscriptionInfo.endDate - new Date()) / (1000 * 60 * 60 * 24)))} jours restants`
                      }
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                    {subscriptionInfo.startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                    {subscriptionInfo.endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div style={{ background: '#e5e7eb', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{
                    background: subscriptionInfo.expired
                      ? '#DC2626'
                      : 'linear-gradient(90deg, #22c55e, #16a34a)',
                    height: '100%',
                    borderRadius: '5px',
                    width: `${Math.min(100, Math.max(0, ((new Date() - subscriptionInfo.startDate) / (subscriptionInfo.endDate - subscriptionInfo.startDate)) * 100))}%`,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            )}

            {/* Expired Access Block */}
            {subscriptionInfo?.expired && user.role !== 'admin' && (
              <div className="card" style={{
                padding: '2rem',
                textAlign: 'center',
                background: '#FEF2F2',
                border: '2px solid #fca5a5',
                borderRadius: '1rem',
                marginBottom: '1.5rem'
              }} data-testid="expired-access-block">
                <Lock size={40} style={{ color: '#DC2626', marginBottom: '1rem' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#DC2626', marginBottom: '0.5rem' }}>
                  Votre accès a expiré
                </h3>
                <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
                  Votre abonnement d'un an est arrivé à terme. Pour continuer à accéder à vos cours et contenus, veuillez nous contacter.
                </p>
                <a href="mailto:apdr.vae@gmail.com" className="btn-primary" style={{ display: 'inline-flex' }}>
                  Contactez-nous pour renouveler
                </a>
              </div>
            )}

            {/* Entitlements Section */}
            {currentFormula && currentFormula !== 'bronze' && !isKit && (
              <>
                {/* RDV Découverte indicator */}
                {decouverteBooking && (
                  <div data-testid="decouverte-indicator" style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1.25rem', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                    border: '1px solid #86EFAC',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      background: '#059669', borderRadius: '50%', width: '32px', height: '32px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Phone size={16} color="white" />
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#065F46' }}>
                        Appel Découverte effectué
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#059669', marginLeft: '0.5rem' }}>
                        le {new Date(decouverteBooking.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {decouverteBooking.time ? ` à ${decouverteBooking.time}` : ''}
                      </span>
                    </div>
                  </div>
                )}

                {/* Counters Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  {entitlements.seances > 0 && (
                  <div className="card" data-testid="card-seances" style={{
                    background: 'linear-gradient(135deg, #EBF5FF 0%, #fff 100%)',
                    border: '1px solid #3B82F620'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{ background: '#3B82F615', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Calendar size={22} color="#3B82F6" />
                      </div>
                      <div>
                        <h3 style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e3a5f' }}>Séances coaching</h3>
                        <p style={{ fontSize: '1.5rem', fontWeight: 800, color: '#3B82F6', lineHeight: 1 }}>
                          {confirmedBookings}<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6B7280' }}>/{entitlements.seances === 999 ? '∞' : entitlements.seances}</span>
                        </p>
                      </div>
                    </div>
                    <div style={{ background: '#e0e7ef', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ background: '#3B82F6', height: '100%', borderRadius: '3px', width: `${entitlements.seances === 999 ? 10 : Math.min(100, (confirmedBookings / entitlements.seances) * 100)}%`, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                  )}

                  <div className="card" data-testid="card-corrections" style={{
                    background: 'linear-gradient(135deg, #F3E8FF 0%, #fff 100%)',
                    border: '1px solid #9b59b620'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{ background: '#9b59b615', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileEdit size={22} color="#9b59b6" />
                      </div>
                      <div>
                        <h3 style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e3a5f', whiteSpace: 'nowrap' }}>Corrections Livret 2</h3>
                        <p style={{ fontSize: '1.5rem', fontWeight: 800, color: '#9b59b6', lineHeight: 1 }}>
                          0<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6B7280' }}>/{entitlements.corrections === 999 ? '∞' : entitlements.corrections}</span>
                        </p>
                      </div>
                    </div>
                    <div style={{ background: '#e8daf0', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ background: '#9b59b6', height: '100%', borderRadius: '3px', width: '0%', transition: 'width 0.3s' }} />
                    </div>
                  </div>

                  {entitlements.appels > 0 && (
                  <div className="card" data-testid="card-appels" style={{
                    background: 'linear-gradient(135deg, #ECFDF5 0%, #fff 100%)',
                    border: '1px solid #10B98120'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{ background: '#10B98115', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Clock size={22} color="#10B981" />
                      </div>
                      <div>
                        <h3 style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e3a5f' }}>Appels explicatifs</h3>
                        <p style={{ fontSize: '1.5rem', fontWeight: 800, color: '#10B981', lineHeight: 1 }}>
                          0<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6B7280' }}>/{entitlements.appels === 999 ? '∞' : entitlements.appels}</span>
                        </p>
                      </div>
                    </div>
                    <div style={{ background: '#d1f0e4', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ background: '#10B981', height: '100%', borderRadius: '3px', width: '0%', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                  )}

                  {entitlements.oralBlanc > 0 && (
                  <div className="card" data-testid="card-oral" style={{
                    background: 'linear-gradient(135deg, #FFF7ED 0%, #fff 100%)',
                    border: '1px solid #F9731620'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{ background: '#F9731615', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Award size={22} color="#F97316" />
                      </div>
                      <div>
                        <h3 style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e3a5f' }}>Oral blanc</h3>
                        <p style={{ fontSize: '1.5rem', fontWeight: 800, color: '#F97316', lineHeight: 1 }}>
                          0<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6B7280' }}>/{entitlements.oralBlanc}</span>
                        </p>
                      </div>
                    </div>
                    <div style={{ background: '#fde0c8', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ background: '#F97316', height: '100%', borderRadius: '3px', width: '0%', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                  )}
                </div>

                {/* Formula Includes Summary */}
                <div className="card" style={{
                  marginBottom: '2rem',
                  padding: '1.25rem 1.5rem',
                  background: currentFormula === 'gold'
                    ? 'linear-gradient(135deg, #fdf6e3 0%, #fff 100%)'
                    : 'linear-gradient(135deg, #f0e6f6 0%, #fff 100%)',
                  border: `1px solid ${currentFormula === 'gold' ? '#d4af3730' : '#9b59b630'}`
                }} data-testid="formula-includes">
                  <h3 style={{
                    fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem',
                    color: currentFormula === 'gold' ? '#b8960c' : '#8e44ad'
                  }}>
                    Votre formule {entitlements.label} comprend :
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '0.5rem'
                  }}>
                    {entitlements.includes.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <CheckCircle size={16} style={{
                          flexShrink: 0,
                          color: currentFormula === 'gold' ? '#d4af37' : '#9b59b6'
                        }} />
                        <span className="body-small">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Bronze - No entitlements, show upgrade prompt */}
            {currentFormula === 'bronze' && !isKit && user.role !== 'admin' && user.enrolledPrograms.length > 0 && (
              <div className="card" style={{
                marginBottom: '2rem', padding: '1.5rem',
                background: 'linear-gradient(135deg, #f5e6d3 0%, #fff 100%)',
                border: '1px solid #d4a57430'
              }} data-testid="bronze-info">
                <h3 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#8B6914' }}>
                  Votre formule Bronze comprend :
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                  {(programEntitlements.bronze.includes || []).map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, color: '#c4956a' }} />
                      <span className="body-small">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="body-small" style={{ color: '#8B6914', fontStyle: 'italic', marginBottom: '1rem' }}>
                  Passez à Argent ou Or pour bénéficier de séances de coaching, corrections et oraux blancs.
                </p>
                <Link to={`/programs/${selectedProgramId}`} className="btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  Voir les autres formules
                </Link>
              </div>
            )}

            {/* Kit - Show kit-specific info */}
            {isKit && currentKitData && (
              <div className="card" style={{
                marginBottom: '2rem', padding: '1.5rem',
                background: 'linear-gradient(135deg, #EBF5FF 0%, #fff 100%)',
                border: '2px solid #3B82F630'
              }} data-testid="kit-info">
                <h3 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#1e40af' }}>
                  Votre {currentKitData.title || `Kit Bloc ${kitBlocNum}`} comprend :
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {currentKitData.includes && currentKitData.includes.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, color: '#3B82F6' }} />
                      <span className="body-small">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="body-small" style={{ color: '#1e40af', fontStyle: 'italic', marginBottom: '1rem' }}>
                  Besoin d'un accompagnement plus complet ? Découvrez nos formules Argent et Or.
                </p>
                <Link to={`/programs/${selectedProgramId}`} className="btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  Voir les formules complètes
                </Link>
              </div>
            )}


            {/* Admin sees all counters */}
            {user.role === 'admin' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div className="card" style={{ background: 'linear-gradient(135deg, #EBF5FF 0%, #fff 100%)', border: '1px solid #3B82F620' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Calendar size={22} color="#3B82F6" />
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: '0.85rem' }}>Séances</h3>
                      <p style={{ fontSize: '1.25rem', fontWeight: 800, color: '#3B82F6' }}>{confirmedBookings}</p>
                    </div>
                  </div>
                </div>
                <div className="card" style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #fff 100%)', border: '1px solid #9b59b620' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <FileEdit size={22} color="#9b59b6" />
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: '0.85rem' }}>Corrections</h3>
                      <p style={{ fontSize: '1.25rem', fontWeight: 800, color: '#9b59b6' }}>0</p>
                    </div>
                  </div>
                </div>
                <div className="card" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #fff 100%)', border: '1px solid #10B98120' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Clock size={22} color="#10B981" />
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: '0.85rem' }}>Appels</h3>
                      <p style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10B981' }}>0</p>
                    </div>
                  </div>
                </div>
                <div className="card" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #fff 100%)', border: '1px solid #F9731620' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Award size={22} color="#F97316" />
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: '0.85rem' }}>Oral blanc</h3>
                      <p style={{ fontSize: '1.25rem', fontWeight: 800, color: '#F97316' }}>0</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DEAP Blocs Overview */}
            {selectedProgramId === 'deap' ? (
              <div style={{ marginBottom: '2rem' }}>
                {/* Livret 2 Annoté - Premier élément */}
                <div 
                  className="card hover-lift"
                  style={{ 
                    marginBottom: '2rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '1.5rem'
                  }}
                  data-testid="livret2-download"
                >
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '1rem',
                      width: '70px',
                      height: '70px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <FileText size={36} color="white" />
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        {isKit 
                          ? (currentKitData ? `Livret 2 ${selectedProgramId === 'deeje' ? 'DEEJE' : selectedProgramId === 'bac-assp' ? 'BAC ASSP' : 'DEAP'} — ${currentKitData.title.replace(/^Kit \d+ — /, '')}` : `Livret 2 ${selectedProgramId === 'deeje' ? 'DEEJE' : selectedProgramId === 'bac-assp' ? 'BAC ASSP' : 'DEAP'} — Bloc ${kitBlocNum}`)
                          : `Livret 2 ${selectedProgramId === 'deeje' ? 'DEEJE' : selectedProgramId === 'bac-assp' ? 'BAC ASSP' : 'DEAP'} Annoté`}
                      </h2>
                      <p style={{ opacity: 0.9, marginBottom: '0' }}>
                        {isKit 
                          ? (currentKitData ? `Téléchargez le modèle du Livret 2 annoté — ${currentKitData.title.replace(/^Kit \d+ — /, '')}.` : `Téléchargez le modèle du Livret 2 annoté pour le Bloc ${kitBlocNum} avec conseils de rédaction.`)
                          : 'Téléchargez votre modèle de Livret 2 avec annotations et conseils de rédaction.'
                        }
                      </p>
                    </div>
                    <a
                      href={isKit 
                        ? (selectedProgramId === 'deeje' ? `/downloads/deeje/${kitId}.pdf` : selectedProgramId === 'bac-assp' ? '/downloads/bac-assp/livret2-bac-assp-annote.pdf' : kitDownloadPath) 
                        : (selectedProgramId === 'deeje' ? '/downloads/livret_2_DEEJE_annote.docx' : selectedProgramId === 'bac-assp' ? '/downloads/bac-assp/livret2-bac-assp-annote.pdf' : '/documents/livret2-deap-annote.docx')}
                      download={isKit ? `Kit-${kitBlocNum}-${selectedProgramId === 'deeje' ? 'DEEJE' : selectedProgramId === 'bac-assp' ? 'BAC-ASSP' : 'DEAP'}.pdf` : (selectedProgramId === 'deeje' ? 'Livret2-DEEJE-Annote.docx' : selectedProgramId === 'bac-assp' ? 'Livret2-BAC-ASSP-Annote.pdf' : 'Livret2-DEAP-Annote.docx')}
                      className="btn-primary"
                      style={{
                        background: 'white',
                        color: '#667eea',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '0.5rem',
                        textDecoration: 'none',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.success('Téléchargement du Livret 2 en cours...');
                      }}
                    >
                      <Download size={20} />
                      Télécharger
                    </a>
                  </div>
                </div>

                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  {isKit ? `Bloc ${kitBlocNum} — DEAP` : 'Vue d\'ensemble des Blocs DEAP'}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Bloc 1 */}
                  {(!isKit || kitBlocNum === '1') && (
                  <div 
                    className="card hover-lift"
                    style={{ 
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #FFF5F7 0%, #FFFFFF 100%)',
                      borderLeft: '4px solid #E75480'
                    }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bloc1-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #E75480 0%, #FF6B9D 100%)',
                        borderRadius: '0.75rem',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}>
                        1
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#E75480' }}>
                          BLOC 1 - Accompagnement et soins de l'enfant
                        </h3>
                        <p className="body-small" style={{ color: '#666' }}>
                          Activités 1, 1bis, 2 • Vie quotidienne et vie sociale
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span className="caption" style={{ background: '#E7548020', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#E75480' }}>
                            3 activités
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Bloc 2 */}
                  {(!isKit || kitBlocNum === '2') && (
                  <div 
                    className="card hover-lift"
                    style={{ 
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)',
                      borderLeft: '4px solid #3B82F6'
                    }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bloc2-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
                        borderRadius: '0.75rem',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}>
                        2
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#3B82F6' }}>
                          BLOC 2 - État clinique et soins adaptés
                        </h3>
                        <p className="body-small" style={{ color: '#666' }}>
                          Activités 3, 4, 5 • Observation, soins et déplacements
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span className="caption" style={{ background: '#3B82F620', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#3B82F6' }}>
                            3 activités
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Bloc 3 */}
                  {(!isKit || kitBlocNum === '3') && (
                  <div 
                    className="card hover-lift"
                    style={{ 
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)',
                      borderLeft: '4px solid #F97316'
                    }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bloc3-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
                        borderRadius: '0.75rem',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}>
                        3
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#F97316' }}>
                          BLOC 3 - Information et accompagnement
                        </h3>
                        <p className="body-small" style={{ color: '#666' }}>
                          Activités 6, 7 • Communication et formation
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span className="caption" style={{ background: '#F9731620', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#F97316' }}>
                            2 activités
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Bloc 4 */}
                  {(!isKit || kitBlocNum === '4') && (
                  <div 
                    className="card hover-lift"
                    style={{ 
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
                      borderLeft: '4px solid #10B981'
                    }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bloc4-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                        borderRadius: '0.75rem',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}>
                        4
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#10B981' }}>
                          BLOC 4 - Entretien de l'environnement
                        </h3>
                        <p className="body-small" style={{ color: '#666' }}>
                          Activités 8, 9 • Locaux, matériels et anomalies
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span className="caption" style={{ background: '#10B98120', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#10B981' }}>
                            2 activités
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Bloc 5 */}
                  {(!isKit || kitBlocNum === '5') && (
                  <div 
                    className="card hover-lift"
                    style={{ 
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)',
                      borderLeft: '4px solid #14B8A6'
                    }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bloc5-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #14B8A6 0%, #2DD4BF 100%)',
                        borderRadius: '0.75rem',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}>
                        5
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#14B8A6' }}>
                          BLOC 5 - Transmission et organisation
                        </h3>
                        <p className="body-small" style={{ color: '#666' }}>
                          Activités 10, 11 • Observations et équipe pluri-professionnelle
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span className="caption" style={{ background: '#14B8A620', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#14B8A6' }}>
                            2 activités
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            ) : selectedProgramId === 'deeje' ? (
              /* DEEJE - Vue d'ensemble des 4 DC et 8 Blocs */
              <div style={{ marginBottom: '2rem' }}>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  {isKit && !kitHasElearning ? `Votre Kit — ${currentKitData?.title || 'Kit ' + kitBlocNum}` : 'Vue d\'ensemble des Domaines de Compétences DEEJE'}
                </h2>

                {/* Download section for file-only DEEJE kits (Kit 1, Kit 6) */}
                {isKit && !kitHasElearning && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                    <div 
                      className="card hover-lift"
                      style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '1.5rem'
                      }}
                      data-testid="kit-file-download"
                    >
                      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: '1rem',
                          width: '70px',
                          height: '70px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <FileText size={36} color="white" />
                        </div>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            {currentKitData?.title || `Kit ${kitBlocNum}`}
                          </h2>
                          <p style={{ opacity: 0.9, marginBottom: '0' }}>
                            {currentKitData?.description || `Téléchargez votre fichier pour le Kit ${kitBlocNum}.`}
                          </p>
                        </div>
                        <a
                          href={kitDownloadPath}
                          download={`Kit-${kitBlocNum}-DEEJE.pdf`}
                          className="btn-primary"
                          style={{
                            background: 'white',
                            color: '#667eea',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '0.5rem',
                            textDecoration: 'none',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toast.success('Téléchargement en cours...');
                          }}
                        >
                          <Download size={20} />
                          Télécharger
                        </a>
                      </div>
                    </div>

                    {/* Option to buy a session for kits with optionRdv */}
                    {currentKitData?.optionRdv && (
                      <div className="card" style={{
                        padding: '1.5rem',
                        background: 'linear-gradient(135deg, #FFF7ED 0%, #fff 100%)',
                        border: '1px solid #F9731630'
                      }} data-testid="kit-buy-session">
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{
                            background: '#FFF7ED',
                            borderRadius: '0.75rem',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            border: '1px solid #F9731630'
                          }}>
                            <Calendar size={24} color="#F97316" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>
                              Besoin d'un accompagnement personnalisé ?
                            </h3>
                            <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>
                              Réservez une séance individuelle de 45 min avec votre coach VAE pour {currentKitData?.optionRdvPrice || 80}€.
                            </p>
                          </div>
                          <button
                            className="btn-primary"
                            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            data-testid="buy-kit-session-btn"
                            onClick={async () => {
                              try {
                                const res = await createCheckout({
                                  programId: selectedProgramId,
                                  formula: 'kit',
                                  kitId: `seance-${kitId}`,
                                  kitTitle: `Séance individuelle — ${currentKitData?.title || 'Kit ' + kitBlocNum}`,
                                  price: currentKitData?.optionRdvPrice || 80,
                                  origin: window.location.origin,
                                  email: user.email,
                                  nom: user.name?.split(' ').slice(1).join(' ') || '',
                                  prenom: user.name?.split(' ')[0] || '',
                                  telephone: user.phone || user.telephone || ''
                                });
                                if (res.url) window.location.href = res.url;
                              } catch (err) {
                                toast.error('Erreur lors de la création du paiement');
                              }
                            }}
                          >
                            <ShoppingBag size={18} />
                            Acheter une séance — {currentKitData?.optionRdvPrice || 80}€
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Kit includes list */}
                    {currentKitData?.includes && currentKitData.includes.length > 0 && (
                      <div className="card" style={{ padding: '1.5rem' }}>
                        <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>
                          Contenu de votre kit
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {currentKitData.includes.map((item, i) => (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                              <CheckCircle size={16} color="#10B981" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {(!isKit || kitHasElearning) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                  {/* PÉDAGOGUES - hidden for kits with specific dcIds that don't include pedagogues */}
                  {(!isKit || !currentKitData?.dcIds || currentKitData.dcIds.includes('pedagogues')) && (
                  <div style={{ 
                    border: '2px solid #7C3AED',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #7C3AED 0%, #9F67FF 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        Les principaux pédagogues du DEEJE
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Montessori, Pikler, Vygotsky, Bowlby, Winnicott, Piaget, Rogers...</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #7C3AED', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#7C3AED', fontWeight: '600' }}>PÉDAGOGUES</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          16 pédagogues et théoriciens de référence
                        </h4>
                        <span className="caption" style={{ color: '#2A9D8F' }}>134 slides - Disponible</span>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* Glossaire Professionnel - hidden for kits with specific dcIds that don't include glossaire */}
                  {(!isKit || !currentKitData?.dcIds || currentKitData.dcIds.includes('glossaire')) && (
                  <div style={{ 
                    border: '2px solid #0D9488',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        Glossaire Professionnel Officiel
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Vocabulaire essentiel du DEEJE</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #0D9488', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#0D9488', fontWeight: '600' }}>GLOSSAIRE</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          56 termes professionnels essentiels
                        </h4>
                        <span className="caption" style={{ color: '#0D9488' }}>21 slides - Disponible</span>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* LOIS - shown for formulas and kits that include lois */}
                  {(!isKit || !currentKitData?.dcIds || currentKitData.dcIds.includes('lois')) && (
                  <div style={{ 
                    border: '2px solid #1E40AF',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        Les Lois et Textes de Référence
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Cadre réglementaire de l'accueil du jeune enfant</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #1E40AF', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#1E40AF', fontWeight: '600' }}>LOIS</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          22 textes de référence essentiels
                        </h4>
                        <span className="caption" style={{ color: '#1E40AF' }}>23 slides - Disponible</span>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* DC1-DC4 sections - hidden for kits that don't include dc1 */}
                  {(!isKit || !currentKitData?.dcIds || currentKitData.dcIds.includes('dc1')) && (
                  <>
                  {/* DC1 - Accueil et accompagnement */}
                  <div style={{ 
                    border: '2px solid #E75480',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #E75480 0%, #FF6B9D 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        DC1 - Accueil et accompagnement du jeune enfant et de sa famille
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Blocs de compétences 1 et 2</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #E75480', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#E75480', fontWeight: '600' }}>BLOC 1</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Accompagnement individuel et collectif du jeune enfant
                        </h4>
                        <span className="caption" style={{ color: '#2A9D8F' }}>{allCourses.find(c => c.id === 'course-deeje-bloc1')?.slideCount || '—'} slides - Disponible</span>
                      </div>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #E75480', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#E75480', fontWeight: '600' }}>BLOC 2</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Projet d'accompagnement de la famille
                        </h4>
                        <span className="caption" style={{ color: '#999' }}>Inclus dans la présentation DC1</span>
                      </div>
                    </div>
                  </div>

                  {/* DC2 - Action éducative */}
                  <div style={{ 
                    border: '2px solid #3B82F6',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        DC2 - Action éducative en direction du jeune enfant
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Blocs de compétences 3 et 4</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #3B82F6', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: '600' }}>BLOC 3</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Conception et conduite du projet éducatif
                        </h4>
                        <span className="caption" style={{ color: '#2A9D8F' }}>{allCourses.find(c => c.id === 'course-deeje-bloc3')?.slideCount || '—'} slides - Disponible</span>
                      </div>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #3B82F6', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: '600' }}>BLOC 4</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Prévention et santé du jeune enfant
                        </h4>
                        <span className="caption" style={{ color: '#999' }}>Inclus dans la présentation DC2</span>
                      </div>
                    </div>
                  </div>

                  {/* DC3 - Communication professionnelle */}
                  <div style={{ 
                    border: '2px solid #F97316',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        DC3 - Communication professionnelle
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Blocs de compétences 5 et 6</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #F97316', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#F97316', fontWeight: '600' }}>BLOC 5</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Expression et communication écrite et orale
                        </h4>
                        <span className="caption" style={{ color: '#2A9D8F' }}>{allCourses.find(c => c.id === 'course-deeje-bloc5')?.slideCount || '—'} slides - Disponible</span>
                      </div>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #F97316', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#F97316', fontWeight: '600' }}>BLOC 6</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Communication professionnelle en travail social
                        </h4>
                        <span className="caption" style={{ color: '#999' }}>Inclus dans la présentation DC3</span>
                      </div>
                    </div>
                  </div>

                  {/* DC4 - Dynamiques interinstitutionnelles */}
                  <div style={{ 
                    border: '2px solid #10B981',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        DC4 - Dynamiques interinstitutionnelles, partenariats et réseaux
                      </h3>
                      <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Blocs de compétences 7 et 8</p>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #10B981', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#10B981', fontWeight: '600' }}>BLOC 7</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Connaissance et analyse des contextes institutionnels
                        </h4>
                        <span className="caption" style={{ color: '#2A9D8F' }}>{allCourses.find(c => c.id === 'course-deeje-bloc7')?.slideCount || '—'} slides - Disponible</span>
                      </div>
                      <div 
                        className="card hover-lift"
                        style={{ cursor: 'pointer', margin: 0, borderLeft: '3px solid #10B981', padding: '1.25rem' }}
                        onClick={() => setActiveTab('courses')}
                      >
                        <span style={{ fontSize: '0.75rem', color: '#10B981', fontWeight: '600' }}>BLOC 8</span>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                          Mobilisation des acteurs et partenaires
                        </h4>
                        <span className="caption" style={{ color: '#999' }}>Inclus dans la présentation DC4</span>
                      </div>
                    </div>
                  </div>

                  </>
                  )}
                </div>
                )}
              </div>
            ) : selectedProgramId === 'bac-assp' ? (
              <div style={{ marginBottom: '2rem' }}>
                {/* Livret 2 BAC ASSP */}
                <div 
                  className="card hover-lift"
                  style={{ 
                    marginBottom: '2rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '1.5rem'
                  }}
                  data-testid="livret2-bac-assp-download"
                >
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '1rem',
                      width: '70px',
                      height: '70px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <FileText size={36} color="white" />
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        Livret 2 BAC ASSP Annoté
                      </h2>
                      <p style={{ opacity: 0.9, marginBottom: '0' }}>
                        Téléchargez votre modèle de Livret 2 avec annotations et conseils de rédaction.
                      </p>
                    </div>
                    <a
                      href="/downloads/bac-assp/livret2-bac-assp-annote.pdf"
                      download="Livret2-BAC-ASSP-Annote.pdf"
                      className="btn-primary"
                      style={{
                        background: 'white',
                        color: '#667eea',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '0.5rem',
                        textDecoration: 'none',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.success('Téléchargement du Livret 2 en cours...');
                      }}
                    >
                      <Download size={20} />
                      Télécharger
                    </a>
                  </div>
                </div>

                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  Vue d'ensemble des Blocs BAC ASSP
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Bloc 1 */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)', borderLeft: '4px solid #8B5CF6' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-bloc1-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>1</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#8B5CF6' }}>BLOC 1 - Accompagner la personne</h3>
                        <p className="body-small" style={{ color: '#666' }}>Approche globale, communication, projet individualisé, accompagnement quotidien</p>
                        <span className="caption" style={{ background: '#8B5CF620', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#8B5CF6', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>

                  {/* Bloc 2 */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #ECFEFF 0%, #FFFFFF 100%)', borderLeft: '4px solid #06B6D4' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-bloc2-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>2</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#06B6D4' }}>BLOC 2 - Soins d'hygiène, de confort et sécurité</h3>
                        <p className="body-small" style={{ color: '#666' }}>Soins, prévention escarres, environnement, surveillance, alimentation, domicile</p>
                        <span className="caption" style={{ background: '#06B6D420', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#06B6D4', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>

                  {/* Bloc 3 */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)', borderLeft: '4px solid #F59E0B' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-bloc3-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>3</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#F59E0B' }}>BLOC 3 - Travailler et communiquer en équipe pluriprofessionnelle</h3>
                        <p className="body-small" style={{ color: '#666' }}>Équipe, organisation, transmissions, qualité, encadrement</p>
                        <span className="caption" style={{ background: '#F59E0B20', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#F59E0B', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>

                  {/* Bloc 4 */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', borderLeft: '4px solid #10B981' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-bloc4-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>4</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#10B981' }}>BLOC 4 - Éducation à la santé</h3>
                        <p className="body-small" style={{ color: '#666' }}>Concepts, méthodologie, thèmes santé, prévention</p>
                        <span className="caption" style={{ background: '#10B98120', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#10B981', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>

                  {/* PSE */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)', borderLeft: '4px solid #3B82F6' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-pse-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>PSE</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#3B82F6' }}>PSE - Prévention Santé Environnement</h3>
                        <p className="body-small" style={{ color: '#666' }}>Risques, prévention, premiers secours</p>
                        <span className="caption" style={{ background: '#3B82F620', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#3B82F6', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>

                  {/* Outils */}
                  <div 
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #FEF2F2 0%, #FFFFFF 100%)', borderLeft: '4px solid #EF4444' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="bac-assp-outils-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '0.65rem', textAlign: 'center', lineHeight: '1.1' }}>OUTILS</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#EF4444' }}>Outils transversaux et examens</h3>
                        <p className="body-small" style={{ color: '#666' }}>Glossaire, PFMP, préparation aux épreuves</p>
                        <span className="caption" style={{ background: '#EF444420', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#EF4444', marginTop: '0.5rem', display: 'inline-block' }}>2 activités</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : selectedProgramId === 'tp' ? (
              <>
                {/* Documents TP IEPE */}
                <div style={{
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  marginBottom: '2rem',
                  background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                  color: 'white',
                  padding: '1.5rem'
                }}
                data-testid="tp-documents-download"
                >
                  <h3 style={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1.1rem' }}>Documents à télécharger</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                    <a
                      href="https://customer-assets.emergentagent.com/job_7ebc9951-ac6a-45ad-bbd9-5b722966c82a/artifacts/xj0jlhb7_livret%202%20en%20word.docx"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: '0.75rem', background: 'rgba(255,255,255,0.15)', textDecoration: 'none', color: 'white', transition: 'background 0.2s' }}
                      data-testid="tp-livret2-download"
                    >
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FileText size={22} />
                      </div>
                      <div>
                        <p style={{ fontWeight: 600, margin: '0 0 0.15rem 0', fontSize: '0.9rem' }}>Livret 2 détaillé</p>
                        <p style={{ opacity: 0.85, margin: 0, fontSize: '0.78rem' }}>Format Word (.docx)</p>
                      </div>
                      <Download size={18} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                    </a>
                    <a
                      href="https://customer-assets.emergentagent.com/job_7ebc9951-ac6a-45ad-bbd9-5b722966c82a/artifacts/8kgierl9_modele%20diaporama_tp_iepe.pptx"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: '0.75rem', background: 'rgba(255,255,255,0.15)', textDecoration: 'none', color: 'white', transition: 'background 0.2s' }}
                      data-testid="tp-diaporama-download"
                    >
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Presentation size={22} />
                      </div>
                      <div>
                        <p style={{ fontWeight: 600, margin: '0 0 0.15rem 0', fontSize: '0.9rem' }}>Trame du diaporama</p>
                        <p style={{ opacity: 0.85, margin: 0, fontSize: '0.78rem' }}>Format PowerPoint (.pptx)</p>
                      </div>
                      <Download size={18} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                    </a>
                  </div>
                </div>

                {/* Vue d'ensemble des 3 blocs */}
                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  Vue d'ensemble des Blocs TP IEPE
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)', borderLeft: '4px solid #F97316' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="tp-bloc1-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>1</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#F97316' }}>BLOC 1 — Accueil et besoins fondamentaux</h3>
                        <p className="body-small" style={{ color: '#666' }}>Virginia Henderson, observation, besoins physiologiques, affectifs et relationnels</p>
                      </div>
                    </div>
                  </div>
                  <div
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)', borderLeft: '4px solid #8B5CF6' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="tp-bloc2-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>2</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#8B5CF6' }}>BLOC 2 — Accompagner le développement</h3>
                        <p className="body-small" style={{ color: '#666' }}>Espace éducatif, activités intentionnelles, théoriciens (Piaget, Wallon, Vygotski)</p>
                      </div>
                    </div>
                  </div>
                  <div
                    className="card hover-lift"
                    style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', borderLeft: '4px solid #10B981' }}
                    onClick={() => setActiveTab('courses')}
                    data-testid="tp-bloc3-overview"
                  >
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)', borderRadius: '0.75rem', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>3</div>
                      <div style={{ flex: 1 }}>
                        <h3 className="heading-3" style={{ marginBottom: '0.25rem', color: '#10B981' }}>BLOC 3 — Accompagner les parents</h3>
                        <p className="body-small" style={{ color: '#666' }}>Co-éducation, alliance éducative, parentalité, prévention, inclusion, CNV</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="card" style={{ marginBottom: '2rem' }}>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  Continuer votre apprentissage
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {userCourses.filter(c => !c.isHeader).map((course) => (
                    <div
                      key={course.id}
                      className="card accent-grey hover-lift"
                      style={{ 
                        cursor: course.locked ? 'not-allowed' : 'pointer',
                        opacity: course.locked ? 0.6 : 1
                      }}
                      onClick={() => handleCourseClick(course)}
                    >
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div style={{
                          background: course.locked ? 'var(--bg-section)' : 'var(--accent-purple-200)',
                          borderRadius: '0.5rem',
                          width: '60px',
                          height: '60px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {course.locked ? <Lock size={24} /> : 
                           course.type === 'video' ? <PlayCircle size={24} /> :
                           course.type === 'presentation' ? <Presentation size={24} /> :
                           course.type === 'quiz' ? <CheckCircle size={24} /> :
                           <FileText size={24} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 className="heading-3" style={{ marginBottom: '0.25rem' }}>
                            {course.title}
                          </h3>
                          <p className="body-small" style={{ marginBottom: '0.5rem' }}>
                            {course.description}
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {course.completed && (
                              <span className="caption" style={{ color: 'green' }}>
                                Terminé
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

  );
};

export default OverviewTab;
